import { useCart } from 'hooks/context/cart';
import { useGiftCardCart } from 'hooks/context/giftCardCart';
import { useMerchantState } from 'hooks/context/MerchantState';
import { useRunningOrder } from 'hooks/context/RunningOrder';
import cloneDeep from 'lodash/cloneDeep';
import { Discount } from 'olo-service';
import { useMemo } from 'react';
import { useInvoiceSummary } from 'routes/Checkout/context/InvoiceSummary';
import { LineItems } from './LineItems';

function formatKey(itemUuid: string, modifiers: string[] = []) {
  return [itemUuid, ...modifiers].sort().join(',');
}
/**
 * List of LineItems that are hydrated with discount information from the Invoice response. Uses a similar strategy as the
 * "anagrams" interview question for hashing lineItems for lookup. Each value is a queue of discounts which allows us to handle
 * displaying items with local quantity, which is not returned from the server (the return value is a flat list of line items).
 */
export const InvoiceLineItems: React.FC = () => {
  const { items: cartItems } = useCart();
  const { items: giftCards } = useGiftCardCart();
  const runningOrder = useRunningOrder();
  const { invoiceSummary } = useInvoiceSummary();
  const { showRunningOrderState } = useMerchantState();

  const items = showRunningOrderState ? runningOrder.items : cartItems;

  const discountMap: Record<string, Array<Discount>> = useMemo(() => invoiceSummary?.lineItems?.reduce((record, lineItem) => {
    const key = formatKey(lineItem.id, lineItem.modifierIds);

    if (!record[key]) {
      return {
        ...record,
        [key]: lineItem.discounts
      };
    }

    return {
      ...record,
      [key]: [...record[key], ...lineItem.discounts]
    };
  }, {} as Record<string, Array<Discount>>) || {}, [invoiceSummary]);

  const hydratedItems = useMemo(() => {
    // Cloning to avoid references when modifying map values
    const clonedMap = cloneDeep(discountMap);

    return items.map((item) => {
      const key = formatKey(item.id, item.modifiers.map((modifier) => modifier.id));

      const discounts = [];
      for (let i = 0; i < item.quantity; i++) {
        /* eslint-disable functional/immutable-data */
        const discount = clonedMap[key]?.pop();
        if (discount) discounts.push(discount);
        /* eslint-enable functional/immutable-data */
      }

      return {
        ...item,
        discounts,
      };
    });
  }, [items, discountMap]);

  return (<LineItems items={hydratedItems} giftCardItems={giftCards} readOnly={showRunningOrderState} />);
};
